import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Link as LinkIcon,
  Pin as PinIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import loteService, { LoteNumeracion } from '../../services/loteService';
import productoService, { Producto } from '../../services/productoService';
import ProductoSearch from '../../components/ProductoSearch';
import GranjaArticuloCodigoField from '../../components/granja/GranjaArticuloCodigoField';
import { getApiErrorMessage } from '../../utils/apiError';

/** Ámbito API: 1 char — G = global, A = por artículo. */
const AMBITO_OPTIONS = [
  { value: 'G', label: 'Global' },
  { value: 'A', label: 'Por artículo' },
] as const;

const MASK_HINT = 'Tokens: {YYYY} {YY} {MM} {DD} {COD} {SEQ:n}  ·  ej. LOT-{YYYY}{MM}-{SEQ:4}';

type FormState = {
  nombre: string;
  mascara: string;
  ambito: string;
  codigoArticulo: string;
  activo: boolean;
};

type ProductoPick = {
  codigoArticulo: string;
  descripcion: string;
  controlHabilitado: boolean;
};

const emptyForm = (): FormState => ({
  nombre: '',
  mascara: 'LOT-{YYYY}{MM}-{SEQ:4}',
  ambito: 'G',
  codigoArticulo: '',
  activo: true,
});

function ambitoLabel(ambito: string | null | undefined): string {
  const a = (ambito || '').trim().toUpperCase();
  if (a === 'A') return 'Por artículo';
  if (a === 'G') return 'Global';
  return ambito || '—';
}

function esAmbitoGlobal(ambito: string | null | undefined): boolean {
  return (ambito || '').trim().toUpperCase() !== 'A';
}

function esAmbitoArticulo(ambito: string | null | undefined): boolean {
  return (ambito || '').trim().toUpperCase() === 'A';
}

function tieneControlLoteSerie(control?: string | null): boolean {
  const c = String(control ?? '').trim().toUpperCase();
  return c === 'L' || c === 'S';
}

async function resolverControlProducto(
  codigo: string,
  controlFromList?: string | null,
): Promise<{ descripcion: string; controlHabilitado: boolean }> {
  if (tieneControlLoteSerie(controlFromList)) {
    return { descripcion: '', controlHabilitado: true };
  }
  try {
    const prod = await productoService.getProducto(codigo);
    return {
      descripcion: String(prod.descripcion ?? '').trim(),
      controlHabilitado: tieneControlLoteSerie(prod.controlLoteSerie),
    };
  } catch {
    return { descripcion: '', controlHabilitado: false };
  }
}

const LotesNumeracionPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [items, setItems] = useState<LoteNumeracion[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<LoteNumeracion | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<string[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [assignMaskId, setAssignMaskId] = useState<number | ''>('');
  const [selectedProducts, setSelectedProducts] = useState<ProductoPick[]>([]);
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await loteService.listNumeracion({ incluirInactivas: true }));
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'Error al cargar máscaras'), { variant: 'error' });
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!dialogOpen || !form.mascara.trim()) {
      setPreview([]);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    const t = setTimeout(() => {
      loteService
        .previewNumeracion({
          mascara: form.mascara.trim(),
          codigoArticulo: form.codigoArticulo.trim() || undefined,
          cantidad: 3,
        })
        .then((nums) => {
          if (!cancelled) setPreview(nums);
        })
        .catch(() => {
          if (!cancelled) setPreview([]);
        })
        .finally(() => {
          if (!cancelled) setPreviewLoading(false);
        });
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [dialogOpen, form.mascara, form.codigoArticulo]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (row: LoteNumeracion) => {
    setEditing(row);
    const ambito = (row.ambito || 'G').trim().toUpperCase().slice(0, 1) || 'G';
    setForm({
      nombre: row.nombre || '',
      mascara: row.mascara || '',
      ambito: ambito === 'A' ? 'A' : 'G',
      codigoArticulo: row.codigoArticulo || '',
      activo: row.activo,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const nombre = form.nombre.trim();
    const mascara = form.mascara.trim();
    if (!nombre || !mascara) {
      enqueueSnackbar('Nombre y máscara son obligatorios', { variant: 'warning' });
      return;
    }
    if (form.ambito === 'A' && !form.codigoArticulo.trim()) {
      enqueueSnackbar('Indicá el artículo para ámbito por artículo', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      let savedId: number | null = null;
      if (editing) {
        const updated = await loteService.updateNumeracion(editing.idLoteNumeracion, {
          nombre,
          mascara,
          ambito: form.ambito,
          codigoArticulo: form.ambito === 'A' ? form.codigoArticulo.trim() : null,
          activo: form.activo,
        });
        savedId = updated.idLoteNumeracion;
        enqueueSnackbar('Máscara actualizada', { variant: 'success' });
      } else {
        const created = await loteService.createNumeracion({
          nombre,
          mascara,
          ambito: form.ambito,
          codigoArticulo: form.ambito === 'A' ? form.codigoArticulo.trim() : null,
        });
        savedId = created.idLoteNumeracion;
        enqueueSnackbar('Máscara creada', { variant: 'success' });
      }

      // Por artículo: asociar automáticamente solo si el producto tiene control lote/serie
      if (form.ambito === 'A' && form.codigoArticulo.trim() && savedId != null) {
        const codigo = form.codigoArticulo.trim();
        const { controlHabilitado } = await resolverControlProducto(codigo);
        if (!controlHabilitado) {
          enqueueSnackbar(
            `Máscara guardada, pero ${codigo} no tiene control de lote/serie. Activá "Por lote" o "Por serie" en el producto para asociarla.`,
            { variant: 'warning', autoHideDuration: 8000 },
          );
        } else {
          try {
            await productoService.setIdLoteNumeracion(codigo, savedId);
          } catch {
            enqueueSnackbar(
              'Máscara guardada, pero no se pudo asociar al producto. Asignalá desde el producto o abajo.',
              { variant: 'warning' },
            );
          }
        }
      }

      setDialogOpen(false);
      await load();
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo guardar la máscara'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: LoteNumeracion) => {
    if (!window.confirm(`¿Eliminar la máscara "${row.nombre}"?`)) return;
    try {
      await loteService.deleteNumeracion(row.idLoteNumeracion);
      enqueueSnackbar('Máscara eliminada', { variant: 'success' });
      await load();
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo eliminar'), { variant: 'error' });
    }
  };

  const handlePickProduct = async (producto: Producto) => {
    const codigo = (producto.codigoArticulo || '').trim();
    if (!codigo) return;
    if (selectedProducts.some((p) => p.codigoArticulo.toUpperCase() === codigo.toUpperCase())) {
      return;
    }

    const controlFromList = producto.controlLoteSerie;
    let controlHabilitado = tieneControlLoteSerie(controlFromList);
    let descripcion = (producto.descripcion || '').trim();

    // El listado a veces no trae controlLoteSerie: confirmar con el detalle
    if (!controlHabilitado) {
      const resolved = await resolverControlProducto(codigo, controlFromList);
      controlHabilitado = resolved.controlHabilitado;
      if (resolved.descripcion) descripcion = resolved.descripcion;
    }

    if (!controlHabilitado) {
      enqueueSnackbar(
        `${codigo} no tiene control de lote/serie habilitado (está en "Sin control").`,
        { variant: 'warning', autoHideDuration: 5000 },
      );
    }

    setSelectedProducts((prev) => [
      ...prev,
      { codigoArticulo: codigo, descripcion, controlHabilitado },
    ]);
  };

  const handleAssign = async () => {
    if (!assignMaskId) {
      enqueueSnackbar('Elegí una máscara', { variant: 'warning' });
      return;
    }
    if (selectedProducts.length === 0) {
      enqueueSnackbar('Agregá al menos un producto', { variant: 'warning' });
      return;
    }

    const mask = items.find((n) => n.idLoteNumeracion === Number(assignMaskId));
    if (!mask || !mask.activo || !esAmbitoGlobal(mask.ambito)) {
      enqueueSnackbar(
        'Solo se pueden asignar máscaras globales. Las de “por artículo” quedan ligadas a su producto al guardarlas.',
        { variant: 'warning', autoHideDuration: 7000 },
      );
      return;
    }

    // Revalidar control por si el listado vino incompleto
    setAssigning(true);
    const verificados: ProductoPick[] = [];
    for (const p of selectedProducts) {
      if (p.controlHabilitado) {
        verificados.push(p);
        continue;
      }
      const resolved = await resolverControlProducto(p.codigoArticulo);
      verificados.push({
        ...p,
        descripcion: resolved.descripcion || p.descripcion,
        controlHabilitado: resolved.controlHabilitado,
      });
    }
    setSelectedProducts(verificados);

    const conControl = verificados.filter((p) => p.controlHabilitado);
    const sinControl = verificados.filter((p) => !p.controlHabilitado);

    if (sinControl.length > 0) {
      const lista = sinControl.map((p) => p.codigoArticulo).join(', ');
      enqueueSnackbar(
        `Sin control de lote/serie (no se asignan): ${lista}. Activá "Por lote" o "Por serie" en cada producto.`,
        { variant: 'warning', autoHideDuration: 10000 },
      );
    }

    if (conControl.length === 0) {
      setAssigning(false);
      enqueueSnackbar('Ningún producto seleccionado tiene control de lote/serie habilitado.', {
        variant: 'error',
      });
      return;
    }

    let ok = 0;
    let fail = 0;
    for (const p of conControl) {
      try {
        await productoService.setIdLoteNumeracion(p.codigoArticulo, Number(assignMaskId));
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setAssigning(false);
    if (ok > 0) {
      enqueueSnackbar(
        `Máscara asignada a ${ok} producto${ok === 1 ? '' : 's'}${fail ? ` (${fail} con error)` : ''}${
          sinControl.length ? `; ${sinControl.length} omitido${sinControl.length === 1 ? '' : 's'} sin control` : ''
        }`,
        { variant: fail || sinControl.length ? 'warning' : 'success' },
      );
      setSelectedProducts(sinControl);
    } else {
      enqueueSnackbar('No se pudo asignar la máscara', { variant: 'error' });
    }
  };

  /** Solo globales activas: las de por artículo no se asignan a otros productos. */
  const mascarasAsignables = items.filter((n) => n.activo && esAmbitoGlobal(n.ambito));

  useEffect(() => {
    if (!assignMaskId) return;
    const ok = items.some(
      (n) =>
        n.idLoteNumeracion === Number(assignMaskId) && n.activo && esAmbitoGlobal(n.ambito),
    );
    if (!ok) setAssignMaskId('');
  }, [assignMaskId, items]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Stack spacing={2}>
        <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ sm: 'center' }}
              spacing={2}
              sx={{ mb: 2 }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Button startIcon={<BackIcon />} onClick={() => navigate('/inventario/lotes')}>
                  Lotes
                </Button>
                <PinIcon sx={{ color: '#1976d2', fontSize: 36 }} />
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                  Máscaras de numeración
                </Typography>
              </Stack>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                Nueva máscara
              </Button>
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Creá plantillas globales o por artículo. Si es por artículo, al guardar se asocia sola a
              ese producto. También podés asignar una máscara global a varios productos más abajo.
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : items.length === 0 ? (
              <Typography color="text.secondary">No hay máscaras cargadas.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Máscara</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Ámbito</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Artículo</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Último N°
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>
                        Acciones
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((row) => (
                      <TableRow key={row.idLoteNumeracion} hover>
                        <TableCell>{row.nombre}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {row.mascara}
                          </Typography>
                        </TableCell>
                        <TableCell>{ambitoLabel(row.ambito)}</TableCell>
                        <TableCell>{row.codigoArticulo || '—'}</TableCell>
                        <TableCell align="right">{row.ultimoNumero}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.activo ? 'Activa' : 'Inactiva'}
                            color={row.activo ? 'success' : 'default'}
                            variant={row.activo ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {esAmbitoGlobal(row.ambito) && row.activo ? (
                            <IconButton
                              size="small"
                              color="primary"
                              title="Usar en asignación a productos"
                              onClick={() => setAssignMaskId(row.idLoteNumeracion)}
                            >
                              <LinkIcon fontSize="small" />
                            </IconButton>
                          ) : (
                            <IconButton
                              size="small"
                              disabled
                              title={
                                esAmbitoArticulo(row.ambito)
                                  ? `Solo para el artículo ${row.codigoArticulo || '—'}`
                                  : 'Máscara inactiva'
                              }
                            >
                              <LinkIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton size="small" color="primary" onClick={() => openEdit(row)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Asignar máscara a productos
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Solo máscaras <strong>globales</strong>. Las de ámbito por artículo se asocian al
              producto indicado al crearlas/editarlas, no desde acá.
            </Typography>
            <Stack spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Máscara global</InputLabel>
                <Select
                  label="Máscara global"
                  value={assignMaskId}
                  onChange={(e) => setAssignMaskId(e.target.value as number | '')}
                >
                  {mascarasAsignables.length === 0 ? (
                    <MenuItem disabled value="">
                      No hay máscaras globales activas
                    </MenuItem>
                  ) : (
                    mascarasAsignables.map((n) => (
                      <MenuItem key={n.idLoteNumeracion} value={n.idLoteNumeracion}>
                        {n.nombre} ({n.mascara})
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  variant="outlined"
                  startIcon={<SearchIcon />}
                  onClick={() => setProductSearchOpen(true)}
                >
                  Buscar y agregar producto
                </Button>
                {selectedProducts.length > 0 && (
                  <Button color="inherit" size="small" onClick={() => setSelectedProducts([])}>
                    Limpiar lista
                  </Button>
                )}
              </Stack>

              {selectedProducts.some((p) => !p.controlHabilitado) && (
                <Alert severity="warning">
                  Sin control de lote/serie:{' '}
                  {selectedProducts
                    .filter((p) => !p.controlHabilitado)
                    .map((p) => p.codigoArticulo)
                    .join(', ')}
                  . Activá &quot;Por lote&quot; o &quot;Por serie&quot; en el producto para poder
                  asignarles máscara.
                </Alert>
              )}

              {selectedProducts.length > 0 && (
                <List dense sx={{ bgcolor: '#fafafa', borderRadius: 1, py: 0 }}>
                  {selectedProducts.map((p) => (
                    <ListItem
                      key={p.codigoArticulo}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() =>
                            setSelectedProducts((prev) =>
                              prev.filter((x) => x.codigoArticulo !== p.codigoArticulo),
                            )
                          }
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={
                          <Stack direction="row" spacing={1} alignItems="center" component="span">
                            <Typography variant="body2" component="span">
                              {p.codigoArticulo} — {p.descripcion || 'Sin descripción'}
                            </Typography>
                            {!p.controlHabilitado && (
                              <Chip size="small" color="warning" label="Sin control" />
                            )}
                          </Stack>
                        }
                        secondary={
                          p.controlHabilitado
                            ? undefined
                            : 'Activá Por lote o Por serie en el maestro del producto'
                        }
                        secondaryTypographyProps={{ color: 'warning.main', variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}

              <Box>
                <Button
                  variant="contained"
                  startIcon={assigning ? <CircularProgress size={16} color="inherit" /> : <LinkIcon />}
                  onClick={handleAssign}
                  disabled={assigning || !assignMaskId || selectedProducts.length === 0}
                >
                  Asignar a {selectedProducts.length || ''} producto
                  {selectedProducts.length === 1 ? '' : 's'}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Editar máscara' : 'Nueva máscara'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nombre"
              required
              fullWidth
              size="small"
              value={form.nombre}
              onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))}
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              label="Máscara"
              required
              fullWidth
              size="small"
              value={form.mascara}
              onChange={(e) => setForm((p) => ({ ...p, mascara: e.target.value }))}
              helperText={MASK_HINT}
              inputProps={{ maxLength: 80 }}
              FormHelperTextProps={{ sx: { fontFamily: 'monospace', fontSize: '0.7rem' } }}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Ámbito</InputLabel>
              <Select
                label="Ámbito"
                value={form.ambito}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    ambito: e.target.value,
                    codigoArticulo: e.target.value === 'G' ? '' : p.codigoArticulo,
                  }))
                }
              >
                {AMBITO_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {form.ambito === 'A' && (
              <GranjaArticuloCodigoField
                label="Artículo"
                value={form.codigoArticulo}
                onChange={(codigo) => setForm((p) => ({ ...p, codigoArticulo: codigo }))}
                dialogTitle="Buscar artículo para la máscara"
              />
            )}
            {editing && (
              <FormControlLabel
                control={
                  <Switch
                    checked={form.activo}
                    onChange={(e) => setForm((p) => ({ ...p, activo: e.target.checked }))}
                    color="primary"
                  />
                }
                label="Activa"
              />
            )}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Vista previa
              </Typography>
              {previewLoading ? (
                <CircularProgress size={18} />
              ) : preview.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Sin preview
                </Typography>
              ) : (
                preview.map((n) => (
                  <Typography key={n} variant="body2" fontFamily="monospace">
                    {n}
                  </Typography>
                ))
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <ProductoSearch
        open={productSearchOpen}
        onClose={() => setProductSearchOpen(false)}
        onSelect={handlePickProduct}
        keepOpenOnSelect
        selectedCodigos={selectedProducts.map((p) => p.codigoArticulo)}
      />
    </Box>
  );
};

export default LotesNumeracionPage;
